import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useThemeClasses } from "@/hooks/useThemeClasses";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Package, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORIAS_PADRAO = [
  "Capinha",
  "Carregador",
  "Fone de Ouvido",
  "Película",
  "Cabo",
  "Power Bank",
  "Suporte",
  "Outros"
];

const AcessorioForm = () => {
  const { slug, id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lojaSlug = slug || user?.loja_slug;
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categorias, setCategorias] = useState(CATEGORIAS_PADRAO);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [showNovaCategoria, setShowNovaCategoria] = useState(false);
  
  const [form, setForm] = useState({
    nome: "",
    descricao: "",
    categoria: "",
    preco: "",
    custo: "",
    quantidade: "",
    codigo_barras: ""
  });

  useEffect(() => {
    fetchCategorias();
    if (isEditing) {
      fetchAcessorio();
    }
  }, [id, lojaSlug]);

  const fetchCategorias = async () => {
    try {
      const res = await axios.get(`${API}/loja/${lojaSlug}/acessorios/categorias`);
      const existingCats = res.data.categorias || [];
      const allCats = [...new Set([...CATEGORIAS_PADRAO, ...existingCats])];
      setCategorias(allCats);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchAcessorio = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/loja/${lojaSlug}/acessorios/${id}`);
      const data = res.data;
      setForm({
        nome: data.nome || "",
        descricao: data.descricao || "",
        categoria: data.categoria || "",
        preco: data.preco?.toString() || "",
        custo: data.custo?.toString() || "",
        quantidade: data.quantidade?.toString() || "0",
        codigo_barras: data.codigo_barras || ""
      });
    } catch (error) {
      toast.error("Erro ao carregar acessório");
      navigate(`/${lojaSlug}/acessorios`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCategoria = () => {
    if (novaCategoria.trim()) {
      const newCat = novaCategoria.trim();
      if (!categorias.includes(newCat)) {
        setCategorias(prev => [...prev, newCat]);
      }
      setForm(prev => ({ ...prev, categoria: newCat }));
      setNovaCategoria("");
      setShowNovaCategoria(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    if (!form.categoria) {
      toast.error("Categoria é obrigatória");
      return;
    }
    if (!form.preco || parseFloat(form.preco) <= 0) {
      toast.error("Preço deve ser maior que zero");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || null,
        categoria: form.categoria,
        preco: parseFloat(form.preco),
        custo: form.custo ? parseFloat(form.custo) : null,
        quantidade: parseInt(form.quantidade) || 0,
        codigo_barras: form.codigo_barras.trim() || null
      };

      if (isEditing) {
        await axios.put(`${API}/loja/${lojaSlug}/acessorios/${id}`, payload);
        toast.success("Acessório atualizado com sucesso");
      } else {
        await axios.post(`${API}/loja/${lojaSlug}/acessorios`, payload);
        toast.success("Acessório cadastrado com sucesso");
      }
      
      navigate(`/${lojaSlug}/acessorios`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar acessório");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#D4AF37]">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="acessorio-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate(`/${lojaSlug}/acessorios`)}
          className="text-gray-400 hover:text-white hover:bg-white/5"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Package className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Outfit']">
              {isEditing ? "Editar Acessório" : "Novo Acessório"}
            </h1>
            <p className="text-gray-400 text-sm">
              {isEditing ? "Atualize os dados do acessório" : "Cadastre um novo acessório"}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <Card className="bg-[#141414] border border-white/5">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label className="text-gray-300">Nome *</Label>
                <Input
                  placeholder="Ex: Capinha iPhone 15 Pro"
                  value={form.nome}
                  onChange={(e) => handleChange("nome", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="input-nome"
                />
              </div>

              {/* Categoria */}
              <div className="space-y-2">
                <Label className="text-gray-300">Categoria *</Label>
                {!showNovaCategoria ? (
                  <div className="flex gap-2">
                    <Select value={form.categoria} onValueChange={(v) => handleChange("categoria", v)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white flex-1">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {categorias.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNovaCategoria(true)}
                      className="border-white/10 text-gray-400 hover:text-white"
                    >
                      Nova
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Nome da nova categoria"
                      value={novaCategoria}
                      onChange={(e) => setNovaCategoria(e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                    />
                    <Button type="button" onClick={handleAddCategoria} className="bg-green-600 hover:bg-green-700">
                      Adicionar
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNovaCategoria(false)}
                      className="border-white/10"
                    >
                      Cancelar
                    </Button>
                  </div>
                )}
              </div>

              {/* Preço */}
              <div className="space-y-2">
                <Label className="text-gray-300">Preço de Venda *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={form.preco}
                  onChange={(e) => handleChange("preco", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="input-preco"
                />
              </div>

              {/* Custo */}
              <div className="space-y-2">
                <Label className="text-gray-300">Custo (opcional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={form.custo}
                  onChange={(e) => handleChange("custo", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>

              {/* Quantidade */}
              <div className="space-y-2">
                <Label className="text-gray-300">Quantidade em Estoque</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.quantidade}
                  onChange={(e) => handleChange("quantidade", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="input-quantidade"
                />
              </div>

              {/* Código de Barras */}
              <div className="space-y-2">
                <Label className="text-gray-300">Código de Barras (opcional)</Label>
                <Input
                  placeholder="Ex: 7891234567890"
                  value={form.codigo_barras}
                  onChange={(e) => handleChange("codigo_barras", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                />
              </div>

              {/* Descrição */}
              <div className="space-y-2 md:col-span-2">
                <Label className="text-gray-300">Descrição (opcional)</Label>
                <Textarea
                  placeholder="Descrição detalhada do produto..."
                  value={form.descricao}
                  onChange={(e) => handleChange("descricao", e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white min-h-[80px]"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4 border-t border-white/5">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/${lojaSlug}/acessorios`)}
                className="border-white/10 text-gray-400 hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#D4AF37] text-black font-bold hover:bg-[#B5952F]"
                data-testid="btn-salvar"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcessorioForm;
